import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  Stack,
} from "@mui/material";
import { useState, useEffect } from "react";
import { Transition } from "../../pages/app/utils/animation";
import { BarChart } from "@mui/x-charts/BarChart";
import { useModals } from "../../store/dialogs/hooks";
import { loadStoiResults } from "../../pages/app/utils/dataLoader";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { convertSeconds } from "../../pages/app/utils/formatTime";

export const StoiResultsDialog = ({ clip }) => {
  const theme = useTheme();
  const { clipDetailsClipId, showStoiResultsDialog, closeStoiResultsDialog } =
    useModals();
  const handleClose = () => closeStoiResultsDialog();

  const [results, setResults] = useState([]);
  const [selectedResultIndex, setSelectedResultIndex] = useState(0);

  // range colors
  const RANGE_COLORS = {
    low: theme.palette.error.main,
    moderate: theme.palette.warning.main,
    good: theme.palette.info.main,
    high: theme.palette.success.main,
  };

  useEffect(() => {
    if (!showStoiResultsDialog || clipDetailsClipId !== clip.id) return;

    const load = async () => {
      const stoiResults = await loadStoiResults(clip.caseId, clip.id);
      if (!stoiResults) console.error("Error loading STOI results");
      setResults(stoiResults || []);
    };
    load();
  }, [clip.caseId, clip.id, showStoiResultsDialog, clipDetailsClipId]);

  useEffect(() => {
    if (!showStoiResultsDialog) {
      setSelectedResultIndex(0);
    }
  }, [showStoiResultsDialog]);

  return (
    <Dialog
      open={showStoiResultsDialog && clipDetailsClipId === clip.id}
      aria-hidden={!showStoiResultsDialog}
      closeAfterTransition={false}
      disableRestoreFocus
      fullWidth
      maxWidth="lg"
      onClose={handleClose}
      slots={{
        transition: Transition,
      }}
    >
      <DialogTitle sx={{ textTransform: "uppercase" }}>
        STOI Results
      </DialogTitle>
      <DialogContent dividers>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            fontWeight={500}
            variant="body2"
            textTransform="uppercase"
            gutterBottom
          >
            Audio Channel for Scoring:
          </Typography>
          <Typography variant="body2" textTransform="uppercase" gutterBottom>
            {clip.description}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            fontWeight={500}
            variant="body2"
            textTransform="uppercase"
            gutterBottom
          >
            STOI Reference:
          </Typography>
          <Typography variant="body2" textTransform="uppercase" gutterBottom>
            {results[selectedResultIndex]?.targetClipName}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            fontWeight={500}
            variant="body2"
            textTransform="uppercase"
            gutterBottom
          >
            Scoring Calculation Time:
          </Typography>
          <Typography variant="body2" textTransform="uppercase" gutterBottom>
            {new Date(results[selectedResultIndex]?.runEpochMs)
              .toLocaleString()
              .replace(", ", " - ")}
          </Typography>
        </Box>
        <Stack gap={0.5}>
          <Typography
            variant="body2"
            textTransform="uppercase"
            sx={{ textDecoration: "underline" }}
          >
            Matched Location
          </Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              fontWeight={500}
              variant="body2"
              textTransform="uppercase"
            >
              Start:
            </Typography>
            <Typography variant="body2" textTransform="uppercase">
              {convertSeconds(
                results[selectedResultIndex]?.overlapStartSeconds,
              )}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography
              fontWeight={500}
              variant="body2"
              textTransform="uppercase"
            >
              End:
            </Typography>
            <Typography variant="body2" textTransform="uppercase">
              {convertSeconds(results[selectedResultIndex]?.overlapEndSeconds)}
            </Typography>
          </Box>
        </Stack>
        <Box height={400} mt={2} width={1}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={1}
          >
            <Tooltip title="Low Intelligibility">
              <Box display="flex" alignItems="center" gap={0.75}>
                <Box
                  borderRadius={1}
                  height={12}
                  width={12}
                  backgroundColor={RANGE_COLORS.low}
                />
                <Typography lineHeight={1} variant="caption">
                  &lt;40&#37;
                </Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Moderate Intelligibility">
              <Box display="flex" alignItems="center" gap={0.75}>
                <Box
                  ml={2}
                  borderRadius={1}
                  height={12}
                  width={12}
                  backgroundColor={RANGE_COLORS.moderate}
                />
                <Typography lineHeight={1} variant="caption">
                  40-60&#37;
                </Typography>
              </Box>
            </Tooltip>
            <Tooltip title="Good Intelligibility">
              <Box display="flex" alignItems="center" gap={0.75}>
                <Box
                  ml={2}
                  borderRadius={1}
                  height={12}
                  width={12}
                  backgroundColor={RANGE_COLORS.good}
                />
                <Typography lineHeight={1} variant="caption">
                  60-80&#37;
                </Typography>
              </Box>
            </Tooltip>
            <Tooltip title="High Intelligibility">
              <Box display="flex" alignItems="center" gap={0.75}>
                <Box
                  ml={2}
                  borderRadius={1}
                  height={12}
                  width={12}
                  backgroundColor={RANGE_COLORS.high}
                />
                <Typography lineHeight={1} variant="caption">
                  &gt;80&#37;
                </Typography>
              </Box>
            </Tooltip>
          </Box>
          <BarChart
            borderRadius={5}
            grid={{ horizontal: true }}
            series={[
              {
                data:
                  results[selectedResultIndex]?.channels.map((channel) =>
                    channel.score?.toFixed(1),
                  ) || [],
                barLabel: "value",
                barLabelPlacement: "center",
              },
            ]}
            xAxis={[
              {
                data:
                  results[selectedResultIndex]?.channels.map(
                    (channel) => `Channel ${channel.channel}`,
                  ) || [],
              },
            ]}
            yAxis={[
              {
                min: 0,
                max: 1,
                colorMap: {
                  type: "piecewise",
                  thresholds: [0.4, 0.6, 0.8, 1.0],
                  colors: [...Object.values(RANGE_COLORS)],
                },
              },
            ]}
            margin={{ bottom: 0, left: -8 }}
            sx={{
              "& .MuiBarLabel-root": {
                dominantBaseline: "ideographic",
                transform: "translateY(10px)",
                fill: theme.palette.common.white,
                fontWeight: "bold",
                fontSize: "14px",
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        {results?.length > 1 && (
          <Stack
            direction="row"
            gap={1}
            sx={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <Tooltip title="Previous result" placement="left">
              <span>
                <IconButton
                  aria-label="back"
                  disabled={
                    results[selectedResultIndex]?.channels?.length === 0 ||
                    selectedResultIndex === 0
                  }
                  onClick={() =>
                    setSelectedResultIndex((prevIndex) =>
                      prevIndex > 0 ? prevIndex - 1 : results.length - 1,
                    )
                  }
                  sx={{
                    backgroundColor: "primary.main",
                    boxShadow: 2,
                    color: "#ffffff",
                    transition: "all 250ms ease-in-out",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                      color: "#e0e0e0",
                    },
                    "&:disabled": {
                      boxShadow: 0,
                    },
                  }}
                >
                  <ArrowBack />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Next result" placement="right">
              <span style={{ marginLeft: 0 }}>
                <IconButton
                  aria-label="forward"
                  disabled={
                    results[selectedResultIndex]?.channels?.length === 0 ||
                    selectedResultIndex === results.length - 1
                  }
                  sx={{
                    backgroundColor: "primary.main",
                    boxShadow: 2,
                    color: "#ffffff",
                    transition: "all 250ms ease-in-out",
                    "&:hover": {
                      backgroundColor: "primary.dark",
                      color: "#e0e0e0",
                    },
                    "&:disabled": {
                      boxShadow: 0,
                    },
                  }}
                  onClick={() =>
                    setSelectedResultIndex((prevIndex) =>
                      prevIndex < results.length - 1 ? prevIndex + 1 : 0,
                    )
                  }
                >
                  <ArrowForward />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        )}
        <Button onClick={handleClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
